import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { compareDateOnlyDesc, formatDateForDisplay } from '../utils/dateFormat';
import { calculateIdealWeight, getIdealHealthValues } from '../utils/healthCalculations';
import ProductSettlementsPanel from '../components/ProductSettlementsPanel';

const CustomerDashboard = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [customer, setCustomer] = useState(null);
  const [healthReadings, setHealthReadings] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [healthReadingsLoading, setHealthReadingsLoading] = useState(false);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('health');
  const [cashbackBalance, setCashbackBalance] = useState(null);
  const [cashbackHistory, setCashbackHistory] = useState([]);
  const [cashbackConsumption, setCashbackConsumption] = useState([]);
  const [cashbackLoading, setCashbackLoading] = useState(false);
  const [healthReports, setHealthReports] = useState([]);
  const [healthReportsLoading, setHealthReportsLoading] = useState(false);
  const [userPrograms, setUserPrograms] = useState([]);
  const [userProgramsLoading, setUserProgramsLoading] = useState(false);
  const [mealPlans, setMealPlans] = useState([]);
  const [mealPlansLoading, setMealPlansLoading] = useState(false);
  const [referrals, setReferrals] = useState([]);
  const [referralsLoading, setReferralsLoading] = useState(false);
  const [showAddHealthReadingModal, setShowAddHealthReadingModal] = useState(false);
  const [newHealthReading, setNewHealthReading] = useState({
    readingDate: new Date().toISOString().split('T')[0],
    weight: '',
    visceralFat: '',
    tsf: '',
    totalFatPercentage: '',
    metaAge: '',
    bmi: '',
    bmr: '',
    skeletalMusclePercentage: ''
  });
  const isSystemCustomer = user?.systemRoles?.some(role => role.code === 'SYSTEM_CUSTOMER');
  const showProgramTransparencyColumns = !isSystemCustomer || userPrograms.every(program => program.transparencyMode !== false);

  useEffect(() => {
    if (!user?.id) {
      return;
    }

    fetchCustomerDetails();
    fetchUserPrograms();
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) {
      return;
    }

    if (activeTab === 'health') {
      fetchHealthReadings();
    } else if (activeTab === 'invoices') {
      fetchInvoices();
    } else if (activeTab === 'documents') {
      fetchDocuments();
    } else if (activeTab === 'reports') {
      fetchHealthReports();
    } else if (activeTab === 'programs') {
      fetchUserPrograms();
    } else if (activeTab === 'cashback') {
      fetchCashbackData();
    } else if (activeTab === 'mealplans') {
      fetchMealPlans();
    } else if (activeTab === 'referals') {
      fetchReferrals();
    }
  }, [activeTab, user?.id]);

  const fetchCustomerDetails = async () => {
    try {
      const response = await axios.get(`/api/customers/${user.id}`);
      setCustomer(response.data);
    } catch (error) {
      console.error('Failed to fetch customer details:', error);
      setError(t('Failed to load your details. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const fetchHealthReadings = async () => {
    try {
      setHealthReadingsLoading(true);
      const response = await axios.get(`/api/customers/${user.id}/health-readings?page=0&size=100`);
      const sortedReadings = [...(response.data.content || [])].sort((first, second) => {
        const dateComparison = compareDateOnlyDesc(first.readingDate, second.readingDate);
        if (dateComparison !== 0) {
          return dateComparison;
        }

        return new Date(second.createdAt || 0).getTime() - new Date(first.createdAt || 0).getTime();
      });
      setHealthReadings(sortedReadings);
    } catch (error) {
      console.error('Failed to fetch health readings:', error);
    } finally {
      setHealthReadingsLoading(false);
    }
  };

  const fetchInvoices = async () => {
    try {
      setInvoicesLoading(true);
      const response = await axios.get(`/api/customers/${user.id}/invoices?page=0&size=100`);
      setInvoices(response.data.content || []);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    } finally {
      setInvoicesLoading(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      setDocumentsLoading(true);
      const response = await axios.get('/api/documents/customer');
      setDocuments(response.data || []);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setDocumentsLoading(false);
    }
  };

  const fetchHealthReports = async () => {
    try {
      setHealthReportsLoading(true);
      const response = await axios.get(`/api/customers/${user.id}/health-reports`);
      setHealthReports(response.data || []);
    } catch (error) {
      console.error('Failed to fetch health reports:', error);
    } finally {
      setHealthReportsLoading(false);
    }
  };

  const fetchUserPrograms = async () => {
    try {
      setUserProgramsLoading(true);
      const response = await axios.get(`/api/user-programs/${user.id}`);
      setUserPrograms(response.data || []);
    } catch (error) {
      console.error('Failed to fetch user programs:', error);
    } finally {
      setUserProgramsLoading(false);
    }
  };

  const fetchCashbackData = async () => {
    try {
      setCashbackLoading(true);
      const [balanceResponse, historyResponse, consumptionResponse] = await Promise.all([
        axios.get('/api/cashback/balance'),
        axios.get('/api/cashback/history'),
        axios.get('/api/cashback/consumption-history')
      ]);
      
      setCashbackBalance(balanceResponse.data);
      setCashbackHistory(historyResponse.data);
      setCashbackConsumption(consumptionResponse.data);
    } catch (error) {
      console.error('Failed to fetch cashback data:', error);
    } finally {
      setCashbackLoading(false);
    }
  };

  const fetchMealPlans = async () => {
    try {
      setMealPlansLoading(true);
      const response = await axios.get(`/api/customer-meal-plans/customer/${user.id}`);
      setMealPlans(response.data || []);
    } catch (error) {
      console.error('Failed to fetch meal plans:', error);
    } finally {
      setMealPlansLoading(false);
    }
  };

  const fetchReferrals = async () => {
    try {
      setReferralsLoading(true);
      const response = await axios.get(`/api/customers/${user.id}/referrals`);
      setReferrals(response.data || []);
    } catch (error) {
      console.error('Failed to fetch referrals:', error);
    } finally {
      setReferralsLoading(false);
    }
  };

  const getDownloadFileName = (response, fallbackFileName) => {
    const contentDisposition = response.headers?.['content-disposition'];
    const utf8FileNameMatch = contentDisposition?.match(/filename\*=UTF-8''([^;]+)/i);
    const asciiFileNameMatch = contentDisposition?.match(/filename="([^\"]+)"/i);

    if (utf8FileNameMatch?.[1]) {
      return decodeURIComponent(utf8FileNameMatch[1]);
    }

    if (asciiFileNameMatch?.[1]) {
      return asciiFileNameMatch[1];
    }

    return fallbackFileName;
  };

  const downloadBlobResponse = (response, fallbackFileName) => {
    const contentType = response.headers?.['content-type'] || 'application/octet-stream';
    const blob = new Blob([response.data], { type: contentType });
    const downloadUrl = window.URL.createObjectURL(blob);
    const resolvedFileName = getDownloadFileName(response, fallbackFileName);
    const link = document.createElement('a');

    link.href = downloadUrl;
    link.download = resolvedFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => {
      window.URL.revokeObjectURL(downloadUrl);
    }, 1000);
  };

  const handleDownloadMealPlan = async (mealPlan) => {
    try {
      if (!mealPlan?.uniqueIdentifier) {
        throw new Error(t('Meal plan link is missing'));
      }
      window.open(`/meal-plan/${mealPlan.uniqueIdentifier}`, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Failed to open meal plan:', error);
      setError(t('Failed to open meal plan. Please try again.'));
    }
  };

  const handleDownloadDocument = async (documentId, fileName) => {
    try {
      const response = await axios.get(`/api/documents/download/${documentId}`, {
        responseType: 'blob',
      });

      downloadBlobResponse(response, fileName);
    } catch (error) {
      console.error('Failed to download document:', error);
      setError(t('Failed to download document. Please try again.'));
    }
  };

  const handleDownloadInvoice = async (invoiceId, fileName) => {
    try {
      const response = await axios.get(`/api/customers/invoices/${invoiceId}/download`, {
        responseType: 'blob',
      });

      downloadBlobResponse(response, fileName);
    } catch (error) {
      console.error('Failed to download invoice:', error);
      setError(t('Failed to download invoice. Please try again.'));
    }
  };

  const handleDownloadHealthReport = async (reportId, fileName) => {
    try {
      const response = await axios.get(`/api/customers/health-reports/${reportId}/download`, {
        responseType: 'blob',
      });

      downloadBlobResponse(response, fileName);
    } catch (error) {
      console.error('Failed to download health report:', error);
      setError(t('Failed to download health report. Please try again.'));
    }
  };

  const handleAddHealthReading = async () => {
    try {
      // Validate required fields
      if (!newHealthReading.readingDate || !newHealthReading.weight) {
        setError(t('Reading date and weight are required.'));
        return;
      }

      const response = await axios.post(`/api/customers/${user.id}/health-readings`, {
        readingDate: newHealthReading.readingDate,
        weight: newHealthReading.weight ? parseFloat(newHealthReading.weight) : null,
        visceralFat: newHealthReading.visceralFat ? parseFloat(newHealthReading.visceralFat) : null,
        tsf: newHealthReading.tsf ? parseFloat(newHealthReading.tsf) : null,
        totalFatPercentage: newHealthReading.totalFatPercentage ? parseFloat(newHealthReading.totalFatPercentage) : null,
        metaAge: newHealthReading.metaAge ? parseInt(newHealthReading.metaAge) : null,
        bmi: newHealthReading.bmi ? parseFloat(newHealthReading.bmi) : null,
        bmr: newHealthReading.bmr ? parseFloat(newHealthReading.bmr) : null,
        skeletalMusclePercentage: newHealthReading.skeletalMusclePercentage ? parseFloat(newHealthReading.skeletalMusclePercentage) : null
      });

      // Reset form and close modal
      setNewHealthReading({
        readingDate: new Date().toISOString().split('T')[0],
        weight: '',
        visceralFat: '',
        tsf: '',
        totalFatPercentage: '',
        metaAge: '',
        bmi: '',
        bmr: '',
        skeletalMusclePercentage: ''
      });
      setShowAddHealthReadingModal(false);
      setError('');

      // Refresh health readings
      fetchHealthReadings();
    } catch (error) {
      console.error('Failed to add health reading:', error);
      setError(error.response?.data?.message || t('Failed to add health reading. Please try again.'));
    }
  };

  const handleHealthReadingInputChange = (field, value) => {
    setNewHealthReading(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (contentType) => {
    if (contentType?.includes('pdf')) return '📄';
    if (contentType?.includes('image')) return '🖼️';
    if (contentType?.includes('video')) return '🎥';
    if (contentType?.includes('audio')) return '🎵';
    if (contentType?.includes('word')) return '📝';
    if (contentType?.includes('excel') || contentType?.includes('spreadsheet')) return '📊';
    if (contentType?.includes('powerpoint') || contentType?.includes('presentation')) return '📋';
    return '📁';
  };

  const isVideoContent = (contentType) => {
    return contentType?.includes('video') || contentType?.toLowerCase().includes('youtube') || contentType?.toLowerCase().includes('vimeo');
  };

  const handleVideoClick = (document) => {
    // If it's a video link, open it in a new tab
    if (document.videoUrl || document.externalUrl) {
      window.open(document.videoUrl || document.externalUrl, '_blank');
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'ONLINE_MEMBER_DOCUMENTS': return t('Online Member Documents');
      case 'CLUB_MEMBER_DOCUMENTS': return t('Club Member Documents');
      case 'MEAL_PLANS': return t('Meal Plans');
      default: return category;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'var(--success)';
      case 'INACTIVE': return 'var(--warning)';
      case 'BLOCKED': return 'var(--error)';
      default: return 'var(--gray-dark)';
    }
  };

  const getInvoiceStatusColor = (status) => {
    switch (status) {
      case 'PAID': return 'var(--success)';
      case 'PENDING': return 'var(--warning)';
      case 'OVERDUE': return 'var(--error)';
      case 'CANCELLED': return 'var(--gray-dark)';
      default: return 'var(--gray-dark)';
    }
  };

  const parseNumericValue = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = parseFloat(String(value).replace(/[^0-9.\-]/g, ''));
    return Number.isNaN(parsed) ? null : parsed;
  };

  const parseRangeValue = (value) => {
    if (!value) return null;
    if (String(value).includes('-')) {
      const [min, max] = String(value).split('-').map(parseNumericValue);
      if (min === null || max === null) return null;
      return { min, max };
    }
    if (String(value).startsWith('<')) {
      const max = parseNumericValue(String(value).slice(1));
      if (max === null) return null;
      return { maxExclusive: max };
    }
    const exact = parseNumericValue(value);
    if (exact === null) return null;
    return { exact };
  };

  const getHealthValueColor = (value, rule) => {
    const numericValue = parseNumericValue(value);
    if (numericValue === null || !rule) {
      return 'inherit';
    }

    if (rule.maxExclusive !== undefined) {
      if (numericValue === rule.maxExclusive) return 'var(--warning)';
      return numericValue > rule.maxExclusive ? 'var(--error)' : 'var(--success)';
    }

    if (rule.min !== undefined && rule.max !== undefined) {
      if (numericValue < rule.min || numericValue > rule.max) return 'var(--error)';
      if (numericValue === rule.min || numericValue === rule.max) return 'var(--warning)';
      return 'var(--success)';
    }

    if (rule.exact !== undefined) {
      if (numericValue === rule.exact) return 'var(--warning)';
      return numericValue > rule.exact ? 'var(--error)' : 'var(--success)';
    }

    return 'inherit';
  };

  const getHealthReadingCellStyle = (value, rule) => ({
    padding: '12px',
    fontWeight: '500',
    color: getHealthValueColor(value, rule)
  });

  if (loading) {
    return (
      <div className="page">
        <div className="loading">{t('Loading your dashboard...')}</div>
      </div>
    );
  }

  return (
    <div className="page">
      <h1 className="page-title">{t('My Dashboard')}</h1>

      {error && (
        <div className="alert alert-error">{error}</div>
      )}

      {/* Personal Information Section - 30% of page */}
      <div className="card" style={{ marginBottom: '30px', minHeight: '30vh' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap',
          marginBottom: '20px'
        }}>
          <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>{t('My Information')}</h3>
          <Link to="/leaderboard" className="btn btn-secondary">
            {t('Leaderboard')}
          </Link>
        </div>
        
        {customer && (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
            gap: '20px'
          }}>
            <div>
              <h4 style={{ color: 'var(--primary-blue)', marginBottom: '10px' }}>{t('Personal Details')}</h4>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('Email')}:</strong> {customer.email}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('Phone')}:</strong> {customer.phone || t('N/A')}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('Gender')}:</strong> {customer.gender ? t(customer.gender) : t('N/A')}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('Address')}:</strong> {customer.address || t('N/A')}
              </div>
            </div>

            <div>
              <h4 style={{ color: 'var(--primary-blue)', marginBottom: '10px' }}>{t('Account Details')}</h4>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('Status')}:</strong> 
                <span style={{
                  color: getStatusColor(customer.status),
                  marginLeft: '5px',
                  fontWeight: '500'
                }}>
                  {customer.status ? t(customer.status) : t('N/A')}
                </span>
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('PC ID/Associate ID')}:</strong> {customer.pcId || t('N/A')}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('Customer Type')}:</strong> {customer.customerType ? t(customer.customerType) : t('N/A')}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('Referred By')}:</strong> {customer.referredBy?.fullName || t('N/A')}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('Joined')}:</strong> {customer.joinedAt ? new Date(customer.joinedAt).toLocaleDateString() : t('N/A')}
              </div>
            </div>

            <div>
              <h4 style={{ color: 'var(--primary-blue)', marginBottom: '10px' }}>{t('Program Details')}</h4>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('Active Program')}:</strong> {(() => {
                  const activeProgram = userPrograms.find(program => program.isActive);
                  return activeProgram ? activeProgram.programName : t('No Active Program');
                })()}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('Checkup Day')}:</strong> {customer.checkupDay || t('N/A')}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('Start Date')}:</strong> {(() => {
                  const activeProgram = userPrograms.find(program => program.isActive);
                  return activeProgram ? formatDateForDisplay(activeProgram.startDate) : t('N/A');
                })()}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('End Date')}:</strong> {(() => {
                  const activeProgram = userPrograms.find(program => program.isActive);
                  return activeProgram ? formatDateForDisplay(activeProgram.endDate) : t('N/A');
                })()}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('Program Discount')}:</strong> {customer.programDiscountPlan?.planName || t('Basic')}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('Product Discount')}:</strong> {customer.productDiscountPlan?.planName || t('Basic (MRP)')}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tabbed Section - 70% of page */}
      <div className="card">
        {/* Tab Navigation */}
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap',
          borderBottom: '2px solid var(--gray-medium)', 
          marginBottom: '20px' 
        }}>
          <button
            onClick={() => setActiveTab('health')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeTab === 'health' ? 'var(--primary-blue)' : 'transparent',
              color: activeTab === 'health' ? 'var(--white)' : 'var(--gray-dark)',
              cursor: 'pointer',
              borderRadius: '6px 6px 0 0',
              fontWeight: '500',
              marginRight: '5px'
            }}
          >
            {t('Health Readings')}
          </button>
          <button
            onClick={() => setActiveTab('mealplans')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeTab === 'mealplans' ? 'var(--primary-blue)' : 'transparent',
              color: activeTab === 'mealplans' ? 'var(--white)' : 'var(--gray-dark)',
              cursor: 'pointer',
              borderRadius: '6px 6px 0 0',
              fontWeight: '500',
              marginRight: '5px'
            }}
          >
            🍽️ {t('Meal Plans')}
          </button>
          <button
            onClick={() => setActiveTab('programs')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeTab === 'programs' ? 'var(--primary-blue)' : 'transparent',
              color: activeTab === 'programs' ? 'var(--white)' : 'var(--gray-dark)',
              cursor: 'pointer',
              borderRadius: '6px 6px 0 0',
              fontWeight: '500',
              marginRight: '5px'
            }}
          >
            🎯 {t('Programs')}
          </button>
          <button
            onClick={() => setActiveTab('reports')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeTab === 'reports' ? 'var(--primary-blue)' : 'transparent',
              color: activeTab === 'reports' ? 'var(--white)' : 'var(--gray-dark)',
              cursor: 'pointer',
              borderRadius: '6px 6px 0 0',
              fontWeight: '500',
              marginRight: '5px'
            }}
          >
            📊 {t('Health Reports')}
          </button>
          <button
            onClick={() => setActiveTab('invoices')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeTab === 'invoices' ? 'var(--primary-blue)' : 'transparent',
              color: activeTab === 'invoices' ? 'var(--white)' : 'var(--gray-dark)',
              cursor: 'pointer',
              borderRadius: '6px 6px 0 0',
              fontWeight: '500',
              marginRight: '5px'
            }}
          >
            {t('Invoices')}
          </button>
          <button
            onClick={() => setActiveTab('cashback')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeTab === 'cashback' ? 'var(--primary-blue)' : 'transparent',
              color: activeTab === 'cashback' ? 'var(--white)' : 'var(--gray-dark)',
              cursor: 'pointer',
              borderRadius: '6px 6px 0 0',
              fontWeight: '500',
              marginRight: '5px'
            }}
          >
            💰 {t('Cashback')}
          </button>
          <button
            onClick={() => setActiveTab('documents')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeTab === 'documents' ? 'var(--primary-blue)' : 'transparent',
              color: activeTab === 'documents' ? 'var(--white)' : 'var(--gray-dark)',
              cursor: 'pointer',
              borderRadius: '6px 6px 0 0',
              fontWeight: '500'
            }}
          >
            {t('My Health Library')}
          </button>
          <button
            onClick={() => setActiveTab('referals')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeTab === 'referals' ? 'var(--primary-blue)' : 'transparent',
              color: activeTab === 'referals' ? 'var(--white)' : 'var(--gray-dark)',
              cursor: 'pointer',
              borderRadius: '6px 6px 0 0',
              fontWeight: '500',
              marginLeft: '5px'
            }}
          >
            {t('Referals')}
          </button>
          <button
            onClick={() => setActiveTab('settlements')}
            style={{
              padding: '12px 24px', border: 'none',
              background: activeTab === 'settlements' ? 'var(--primary-blue)' : 'transparent',
              color: activeTab === 'settlements' ? 'var(--white)' : 'var(--gray-dark)',
              cursor: 'pointer', borderRadius: '6px 6px 0 0', fontWeight: '500', marginLeft: '5px'
            }}
          >
            📦 {t('Product Settlements')}
          </button>
        </div>

        {activeTab === 'settlements' && (
          <ProductSettlementsPanel customerId={user.id} canManage={false} t={t} />
        )}

        {/* Health Readings Tab */}
        {activeTab === 'health' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>{t('My Health Readings')}</h3>
              {customer?.customerType === 'ONLINE' && (
                <button 
                  className="btn btn-primary"
                  onClick={() => setShowAddHealthReadingModal(true)}
                  style={{ fontSize: '14px', padding: '8px 16px' }}
                >
                  ➕ {t('Add Reading')}
                </button>
              )}
            </div>
            
            {healthReadingsLoading ? (
              <div className="loading">{t('Loading health readings...')}</div>
            ) : healthReadings.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
                {customer?.customerType === 'ONLINE' 
                  ? t('No health readings found. Click "Add Reading" to record your first health reading.')
                  : t('No health readings found. Your coach will add readings after your checkups.')
                }
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ 
                  width: '100%', 
                  borderCollapse: 'collapse',
                  fontSize: '14px'
                }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Date')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Weight (kg)')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Visceral Fat')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('TSF')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Total Fat (%)')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Meta Age')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('BMI')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('BMR')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Skeletal Muscle (%)')}</th>
                    </tr>
                    {/* Ideal Values Sub-header */}
                    <tr style={{ backgroundColor: 'var(--light-blue)', fontSize: '12px' }}>
                      {(() => {
                        const resolvedIdealWeight = customer?.idealWeightKg || calculateIdealWeight(customer?.heightCm, customer?.gender);
                        const idealValues = getIdealHealthValues(customer?.gender, resolvedIdealWeight);

                        return (
                          <>
                      <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid var(--gray-medium)', fontWeight: 'bold', color: 'var(--dark-blue)' }}>
                        {t('Ideal Values')}:
                      </th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid var(--gray-medium)', fontWeight: 'bold', color: 'var(--dark-blue)' }}>
                        {idealValues.weight || t('N/A')}
                      </th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid var(--gray-medium)', fontWeight: 'bold', color: 'var(--dark-blue)' }}>
                        {idealValues.visceralFat}
                      </th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid var(--gray-medium)', fontWeight: 'bold', color: 'var(--dark-blue)' }}>
                        {idealValues.tsf}
                      </th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid var(--gray-medium)', fontWeight: 'bold', color: 'var(--dark-blue)' }}>
                        {idealValues.totalFatPercentage}
                      </th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid var(--gray-medium)', fontWeight: 'bold', color: 'var(--dark-blue)' }}>
                        {idealValues.metaAge}
                      </th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid var(--gray-medium)', fontWeight: 'bold', color: 'var(--dark-blue)' }}>
                        {idealValues.bmi}
                      </th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid var(--gray-medium)', fontWeight: 'bold', color: 'var(--dark-blue)' }}>
                        -
                      </th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid var(--gray-medium)', fontWeight: 'bold', color: 'var(--dark-blue)' }}>
                        {idealValues.skeletalMusclePercentage}
                      </th>
                          </>
                        );
                      })()}
                    </tr>
                  </thead>
                  <tbody>
                    {healthReadings.map((reading, index) => {
                      const resolvedIdealWeight = customer?.idealWeightKg || calculateIdealWeight(customer?.heightCm, customer?.gender);
                      const idealValues = getIdealHealthValues(customer?.gender, resolvedIdealWeight);
                      const weightRule = resolvedIdealWeight ? { exact: parseNumericValue(resolvedIdealWeight) } : null;
                      const visceralFatRule = parseRangeValue(idealValues.visceralFat);
                      const tsfRule = parseRangeValue(idealValues.tsf);
                      const totalFatRule = parseRangeValue(idealValues.totalFatPercentage);
                      const bmiRule = parseRangeValue(idealValues.bmi);
                      const skeletalMuscleRule = parseRangeValue(idealValues.skeletalMusclePercentage);

                      return (
                        <tr 
                          key={reading.id}
                          style={{ 
                            backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                            borderBottom: '1px solid var(--gray-medium)'
                          }}
                        >
                          <td style={{ padding: '12px' }}>
                            {formatDateForDisplay(reading.readingDate)}
                          </td>
                          <td style={getHealthReadingCellStyle(reading.weight, weightRule)}>{reading.weight || '-'}</td>
                          <td style={getHealthReadingCellStyle(reading.visceralFat, visceralFatRule)}>{reading.visceralFat || '-'}</td>
                          <td style={getHealthReadingCellStyle(reading.tsf, tsfRule)}>{reading.tsf || '-'}</td>
                          <td style={getHealthReadingCellStyle(reading.totalFatPercentage, totalFatRule)}>{reading.totalFatPercentage || '-'}</td>
                          <td style={{ padding: '12px' }}>{reading.metaAge || '-'}</td>
                          <td style={getHealthReadingCellStyle(reading.bmi, bmiRule)}>{reading.bmi || '-'}</td>
                          <td style={{ padding: '12px' }}>{reading.bmr || '-'}</td>
                          <td style={getHealthReadingCellStyle(reading.skeletalMusclePercentage, skeletalMuscleRule)}>{reading.skeletalMusclePercentage || '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <div>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>{t('My Invoices')}</h3>
            
            {invoicesLoading ? (
              <div className="loading">{t('Loading invoices...')}</div>
            ) : invoices.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
                {t('No invoices found.')}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ 
                  width: '100%', 
                  borderCollapse: 'collapse',
                  fontSize: '14px'
                }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Invoice #')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Date')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Description')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Amount')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Status')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Due Date')}</th>
                      <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid var(--light-blue)' }}>{t('Actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice, index) => (
                      <tr 
                        key={invoice.id}
                        style={{ 
                          backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                          borderBottom: '1px solid var(--gray-medium)'
                        }}
                      >
                        <td style={{ padding: '12px', fontWeight: '500' }}>
                          {invoice.invoiceNumber}
                        </td>
                        <td style={{ padding: '12px' }}>
                          {formatDateForDisplay(invoice.invoiceDate)}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <div>{invoice.description}</div>
                          {invoice.payments && invoice.payments.length > 0 && (
                            <div style={{ fontSize: '11px', color: 'var(--gray-dark)', marginTop: '4px' }}>
                              {t('{count} payment(s) • Balance: ₹{amount}', {
                                count: invoice.payments.length,
                                amount: invoice.remainingBalance || 0
                              })}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px', fontWeight: '500' }}>
                          <div>₹{invoice.totalAmount || invoice.amount}</div>
                          {invoice.totalPaidAmount > 0 && (
                            <div style={{ fontSize: '11px', color: 'var(--success)' }}>
                              {t('Paid: ₹{amount}', { amount: invoice.totalPaidAmount })}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <span style={{
                            color: getInvoiceStatusColor(invoice.status),
                            fontWeight: '500'
                          }}>
                            {invoice.status ? t(invoice.status) : ''}
                          </span>
                        </td>
                        <td style={{ padding: '12px' }}>
                          {invoice.dueDate ? formatDateForDisplay(invoice.dueDate) : t('-')}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          {invoice.hasPdf && (
                            <button 
                              className="btn btn-primary"
                              style={{ fontSize: '11px', padding: '4px 8px' }}
                              onClick={() => handleDownloadInvoice(invoice.id, invoice.pdfFileName || `invoice_${invoice.invoiceNumber}.pdf`)}
                              title={t('Download PDF')}
                            >
                              📄 {t('Download')}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'referals' && (
          <div>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>{t('My Referals')}</h3>

            {referralsLoading ? (
              <div className="loading">{t('Loading referals...')}</div>
            ) : referrals.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
                {t('No referals found.')}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '14px'
                }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Name')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Email')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Phone')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Status')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Joined')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {referrals.map((referral, index) => (
                      <tr
                        key={referral.id}
                        style={{
                          backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                          borderBottom: '1px solid var(--gray-medium)'
                        }}
                      >
                        <td style={{ padding: '12px', fontWeight: '500' }}>{referral.fullName}</td>
                        <td style={{ padding: '12px' }}>{referral.email || t('N/A')}</td>
                        <td style={{ padding: '12px' }}>{referral.phone || t('N/A')}</td>
                        <td style={{ padding: '12px', color: getStatusColor(referral.status), fontWeight: '500' }}>{referral.status ? t(referral.status) : t('N/A')}</td>
                        <td style={{ padding: '12px' }}>{referral.joinedAt ? new Date(referral.joinedAt).toLocaleDateString() : t('N/A')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>{t('My Health Library')}</h3>
            <div style={{ 
              marginBottom: '15px', 
              padding: '10px', 
              background: 'var(--pale-blue)', 
              borderRadius: '6px',
              fontSize: '14px',
              color: 'var(--dark-blue)'
            }}>
              📚 {t('Access documents and resources for {customerType} members', {
                customerType: customer?.customerType ? t(customer.customerType) : t('ONLINE')
              })}
            </div>
            
            {documentsLoading ? (
              <div className="loading">{t('Loading documents...')}</div>
            ) : documents.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
                {t('No documents available for {customerType} members at the moment.', {
                  customerType: customer?.customerType ? t(customer.customerType) : t('ONLINE')
                })}
              </div>
            ) : (
              <div>
                {/* Videos Section */}
                {documents.filter(doc => isVideoContent(doc.contentType)).length > 0 && (
                  <div style={{ marginBottom: '40px' }}>
                    <h4 style={{ 
                      color: 'var(--primary-blue)', 
                      marginBottom: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}>
                      🎥 {t('Health Videos ({count})', { count: documents.filter(doc => isVideoContent(doc.contentType)).length })}
                    </h4>
                    
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
                      gap: '20px',
                      marginBottom: '20px'
                    }}>
                      {documents.filter(doc => isVideoContent(doc.contentType)).map((video) => (
                        <div 
                          key={video.id}
                          style={{
                            border: '1px solid var(--gray-medium)',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            background: 'var(--white)',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            transition: 'transform 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
                          onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                        >
                          {/* Video Thumbnail */}
                          <div style={{ 
                            position: 'relative', 
                            paddingBottom: '56.25%', 
                            height: 0,
                            background: 'var(--gray-light)'
                          }}>
                            {video.videoThumbnailUrl ? (
                              <img 
                                src={video.videoThumbnailUrl}
                                alt="Video thumbnail"
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              display: video.videoThumbnailUrl ? 'none' : 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: 'var(--gray-light)',
                              fontSize: '48px'
                            }}>
                              🎥
                            </div>
                            {/* Play Button Overlay */}
                            <div style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              width: '60px',
                              height: '60px',
                              background: 'rgba(0, 0, 0, 0.7)',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              fontSize: '24px',
                              color: 'white'
                            }}
                            onClick={() => handleVideoClick(video)}
                            >
                              ▶️
                            </div>
                          </div>
                          
                          {/* Video Info */}
                          <div style={{ padding: '15px' }}>
                            <div style={{ 
                              fontWeight: '500', 
                              marginBottom: '8px',
                              fontSize: '14px',
                              lineHeight: '1.4'
                            }}>
                              {video.description || video.originalFileName}
                            </div>
                            <div style={{ 
                              fontSize: '12px', 
                              color: 'var(--gray-dark)',
                              marginBottom: '10px'
                            }}>
                              {t('Added by {name} • {date}', {
                                name: video.createdBy,
                                date: formatDateForDisplay(video.createdAt)
                              })}
                            </div>
                            
                            {/* Action Buttons */}
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between' }}>
                              <button 
                                className="btn btn-primary"
                                style={{ 
                                  fontSize: '11px', 
                                  padding: '6px 12px',
                                  flex: 1
                                }}
                                onClick={() => handleVideoClick(video)}
                                title={t('Watch Video')}
                              >
                                🎥 {t('Watch')}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Documents Section */}
                {documents.filter(doc => !isVideoContent(doc.contentType)).length > 0 && (
                  <div>
                    <h4 style={{ 
                      color: 'var(--primary-blue)', 
                      marginBottom: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}>
                      📄 {t('Documents ({count})', { count: documents.filter(doc => !isVideoContent(doc.contentType)).length })}
                    </h4>
                    
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ 
                        width: '100%', 
                        borderCollapse: 'collapse',
                        fontSize: '14px'
                      }}>
                        <thead>
                          <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Document')}</th>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Category')}</th>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Description')}</th>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Size')}</th>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Uploaded')}</th>
                            <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid var(--light-blue)' }}>{t('Action')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {documents.filter(doc => !isVideoContent(doc.contentType)).map((document, index) => (
                            <tr 
                              key={document.id}
                              style={{ 
                                backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                                borderBottom: '1px solid var(--gray-medium)'
                              }}
                            >
                              <td style={{ padding: '12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span style={{ fontSize: '20px' }}>
                                    {getFileIcon(document.contentType)}
                                  </span>
                                  <div>
                                    <div style={{ fontWeight: '500' }}>
                                      {document.originalFileName}
                                    </div>
                                    <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>
                                      {document.contentType}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td style={{ padding: '12px' }}>
                                <span style={{
                                  background: 'var(--primary-blue)',
                                  color: 'var(--white)',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  fontSize: '12px'
                                }}>
                                  {getCategoryLabel(document.documentCategory)}
                                </span>
                              </td>
                              <td style={{ padding: '12px' }}>
                                {document.description || t('No description')}
                              </td>
                              <td style={{ padding: '12px' }}>
                                {formatFileSize(document.fileSize)}
                              </td>
                              <td style={{ padding: '12px' }}>
                                {formatDateForDisplay(document.createdAt)}
                              </td>
                              <td style={{ padding: '12px', textAlign: 'center' }}>
                                <button 
                                  className="btn btn-primary"
                                  style={{ fontSize: '11px', padding: '4px 8px' }}
                                  onClick={() => handleDownloadDocument(document.id, document.originalFileName)}
                                  title={t('Download Document')}
                                >
                                  ⬇️ {t('Download')}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Show message if no documents or videos */}
                {documents.filter(doc => !isVideoContent(doc.contentType)).length === 0 && 
                 documents.filter(doc => isVideoContent(doc.contentType)).length === 0 && (
                  <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
                    {t('No documents or videos available for {customerType} members at the moment.', {
                      customerType: customer?.customerType ? t(customer.customerType) : t('ONLINE')
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Health Reports Tab */}
        {activeTab === 'reports' && (
          <div>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>{t('My Health Reports')}</h3>
            
            {healthReportsLoading ? (
              <div className="loading">{t('Loading health reports...')}</div>
            ) : healthReports.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
                {t('No health reports found. Your coach will generate reports based on your health readings.')}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ 
                  width: '100%', 
                  borderCollapse: 'collapse',
                  fontSize: '14px'
                }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Report Date')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Generated By')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('File Size')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Created')}</th>
                      <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid var(--light-blue)' }}>{t('Actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {healthReports.map((report, index) => (
                      <tr 
                        key={report.id}
                        style={{ 
                          backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                          borderBottom: '1px solid var(--gray-medium)'
                        }}
                      >
                        <td style={{ padding: '12px', fontWeight: '500' }}>
                          {formatDateForDisplay(report.reportDate)}
                        </td>
                        <td style={{ padding: '12px' }}>
                          {report.generatedBy}
                        </td>
                        <td style={{ padding: '12px' }}>
                          {report.pdfFileSize ? `${Math.round(report.pdfFileSize / 1024)} KB` : t('N/A')}
                        </td>
                        <td style={{ padding: '12px' }}>
                          {new Date(report.createdAt).toLocaleDateString()}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          {report.hasPdf && (
                            <button 
                              className="btn btn-primary"
                              style={{ fontSize: '11px', padding: '4px 8px' }}
                              onClick={() => handleDownloadHealthReport(report.id, report.pdfFileName || `health_report_${report.id}.pdf`)}
                              title={t('Download PDF')}
                            >
                              📄 {t('Download')}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Programs Tab */}
        {activeTab === 'programs' && (
          <div>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>{t('My Programs')}</h3>
            
            {userProgramsLoading ? (
              <div className="loading">{t('Loading programs...')}</div>
            ) : userPrograms.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
                {t('No programs assigned yet. Your coach will assign programs based on your goals.')}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ 
                  width: '100%', 
                  borderCollapse: 'collapse',
                  fontSize: '14px'
                }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Program')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>Start Date</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>End Date</th>
                      {showProgramTransparencyColumns && (
                        <>
                          <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Volume Points')}</th>
                          <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Days Attended')}</th>
                        </>
                      )}
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Status')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Assigned By')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userPrograms.map((userProgram, index) => (
                      <tr 
                        key={userProgram.id}
                        style={{ 
                          backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                          borderBottom: '1px solid var(--gray-medium)'
                        }}
                      >
                        <td style={{ padding: '12px', fontWeight: '500' }}>
                          <div>{userProgram.programName}</div>
                          <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>
                            {userProgram.programCode}
                          </div>
                        </td>
                        <td style={{ padding: '12px' }}>
                          {formatDateForDisplay(userProgram.startDate)}
                        </td>
                        <td style={{ padding: '12px' }}>
                          {formatDateForDisplay(userProgram.endDate)}
                        </td>
                        {showProgramTransparencyColumns && (
                          <>
                            <td style={{ padding: '12px' }}>
                              <div style={{ fontWeight: '500' }}>
                                {userProgram.volumePointConsumption || 0} / {userProgram.totalVolumePoints || 0}
                              </div>
                              <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>
                                {t('Consumption / Total')}
                              </div>
                            </td>
                            <td style={{ padding: '12px' }}>
                              <div style={{ fontWeight: '500' }}>
                                {userProgram.daysAttended || 0}
                              </div>
                              <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>
                                {t('Days Present')}
                              </div>
                            </td>
                          </>
                        )}
                        <td style={{ padding: '12px' }}>
                          <span style={{
                            background: userProgram.isActive ? 'var(--success)' : 'var(--gray-medium)',
                            color: userProgram.isActive ? 'white' : 'var(--gray-dark)',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>
                            {userProgram.isActive ? t('Active') : t('Inactive')}
                          </span>
                        </td>
                        <td style={{ padding: '12px' }}>
                          {userProgram.assignedBy}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Cashback Tab */}
        {activeTab === 'cashback' && (
          <div>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>{t('My Cashback Wallet')}</h3>
            
            {/* Cashback Balance Card */}
            {cashbackBalance && (
              <div style={{
                background: 'linear-gradient(135deg, #48BB78 0%, #38A169 100%)',
                color: 'white',
                padding: '25px',
                borderRadius: '12px',
                marginBottom: '30px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '16px', marginBottom: '10px', opacity: '0.9' }}>
                  {t('Available Cashback Balance')}
                </div>
                <div style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '10px' }}>
                  ₹{cashbackBalance.availableBalance}
                </div>
                <div style={{ fontSize: '14px', opacity: '0.8' }}>
                  {t('Total Earned: ₹{earned} | Total Used: ₹{used}', {
                    earned: cashbackBalance.totalEarned,
                    used: cashbackBalance.totalConsumed
                  })}
                </div>
              </div>
            )}
            
            {cashbackLoading ? (
              <div className="loading">{t('Loading cashback data...')}</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
                {/* Cashback Earned Section */}
                <div>
                  <h4 style={{ color: 'var(--primary-blue)', marginBottom: '15px' }}>
                    💰 {t('Cashback Earned ({count})', { count: cashbackHistory.length })}
                  </h4>
                  
                  {cashbackHistory.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '30px', color: 'var(--gray-dark)' }}>
                      {t('No cashback earned yet. Refer friends to earn cashback!')}
                    </div>
                  ) : (
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {cashbackHistory.map((cashback, index) => (
                        <div 
                          key={cashback.id}
                          style={{
                            border: '1px solid var(--gray-medium)',
                            borderRadius: '8px',
                            padding: '15px',
                            marginBottom: '10px',
                            background: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <div style={{ fontWeight: '500', color: 'var(--success)', fontSize: '16px' }}>
                              +₹{cashback.cashbackAmount}
                            </div>
                            <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>
                              {new Date(cashback.createdAt).toLocaleDateString()}
                            </div>
                          </div>
                          <div style={{ fontSize: '14px', marginBottom: '4px' }}>
                            <strong>{t('Program')}:</strong> {cashback.programName}
                          </div>
                          <div style={{ fontSize: '14px', marginBottom: '4px' }}>
                            <strong>{t('Referral')}:</strong> {cashback.referredCustomerName}
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>
                            {t('Invoice')}: {cashback.invoiceNumber}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Cashback Spent Section */}
                <div>
                  <h4 style={{ color: 'var(--primary-blue)', marginBottom: '15px' }}>
                    💸 {t('Cashback Used ({count})', { count: cashbackConsumption.length })}
                  </h4>
                  
                  {cashbackConsumption.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '30px', color: 'var(--gray-dark)' }}>
                      {t('No cashback used yet.')}
                    </div>
                  ) : (
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {cashbackConsumption.map((consumption, index) => (
                        <div 
                          key={consumption.id}
                          style={{
                            border: '1px solid var(--gray-medium)',
                            borderRadius: '8px',
                            padding: '15px',
                            marginBottom: '10px',
                            background: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <div style={{ fontWeight: '500', color: 'var(--error)', fontSize: '16px' }}>
                              -₹{consumption.consumedAmount}
                            </div>
                            <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>
                              {new Date(consumption.consumedAt).toLocaleDateString()}
                            </div>
                          </div>
                          <div style={{ fontSize: '14px', marginBottom: '4px' }}>
                            <strong>{t('Used for Invoice')}:</strong> {consumption.invoiceNumber}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* My Meal Plans Tab */}
        {activeTab === 'mealplans' && (
          <div>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>{t('My Meal Plans')}</h3>
            
            {mealPlansLoading ? (
              <div className="loading">{t('Loading meal plans...')}</div>
            ) : mealPlans.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
                {t('No meal plans assigned yet. Your coach will assign meal plans based on your goals and preferences.')}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ 
                  width: '100%', 
                  borderCollapse: 'collapse',
                  fontSize: '14px'
                }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Meal Plan Name')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Weight Category')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Food Preference')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Created Date')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Assigned By')}</th>
                      <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid var(--light-blue)' }}>{t('Actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mealPlans.map((mealPlan, index) => (
                      <tr 
                        key={mealPlan.id}
                        style={{ 
                          backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                          borderBottom: '1px solid var(--gray-medium)'
                        }}
                      >
                        <td style={{ padding: '12px', fontWeight: '500' }}>
                          {mealPlan.mealPlanName}
                        </td>
                        <td style={{ padding: '12px' }}>
                          {mealPlan.weightCategory}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <span style={{
                            background: mealPlan.foodPreference === 'VEG' ? 'var(--success)' : 
                                       mealPlan.foodPreference === 'NON_VEG' ? 'var(--error)' : 
                                       mealPlan.foodPreference === 'VEGAN' ? 'var(--primary-blue)' : 'var(--warning)',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}>
                            {mealPlan.foodPreference}
                          </span>
                        </td>
                        <td style={{ padding: '12px' }}>
                          {new Date(mealPlan.createdAt).toLocaleDateString()}
                        </td>
                        <td style={{ padding: '12px' }}>
                          {mealPlan.assignedByName}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          {mealPlan.uniqueIdentifier && (
                            <button 
                              className="btn btn-primary"
                              style={{ fontSize: '11px', padding: '4px 8px' }}
                              onClick={() => handleDownloadMealPlan(mealPlan)}
                              title={t('Open Meal Plan')}
                            >
                              {t('Open')}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add Health Reading Modal */}
      {showAddHealthReadingModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '30px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>{t('Add Health Reading')}</h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  {t('Reading Date')} *
                </label>
                <input
                  type="date"
                  value={newHealthReading.readingDate}
                  onChange={(e) => handleHealthReadingInputChange('readingDate', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid var(--gray-medium)',
                    borderRadius: '4px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  {t('Weight (kg)')} *
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={newHealthReading.weight}
                  onChange={(e) => handleHealthReadingInputChange('weight', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid var(--gray-medium)',
                    borderRadius: '4px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  {t('Total Fat (%)')}
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={newHealthReading.totalFatPercentage}
                  onChange={(e) => handleHealthReadingInputChange('totalFatPercentage', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid var(--gray-medium)',
                    borderRadius: '4px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  {t('Visceral Fat')}
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={newHealthReading.visceralFat}
                  onChange={(e) => handleHealthReadingInputChange('visceralFat', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid var(--gray-medium)',
                    borderRadius: '4px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  {t('BMR (kcal)')}
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={newHealthReading.bmr}
                  onChange={(e) => handleHealthReadingInputChange('bmr', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid var(--gray-medium)',
                    borderRadius: '4px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  {t('BMI')}
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={newHealthReading.bmi}
                  onChange={(e) => handleHealthReadingInputChange('bmi', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid var(--gray-medium)',
                    borderRadius: '4px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  {t('Metabolic Age')}
                </label>
                <input
                  type="number"
                  value={newHealthReading.metaAge}
                  onChange={(e) => handleHealthReadingInputChange('metaAge', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid var(--gray-medium)',
                    borderRadius: '4px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  {t('TSF (mm)')}
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={newHealthReading.tsf}
                  onChange={(e) => handleHealthReadingInputChange('tsf', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid var(--gray-medium)',
                    borderRadius: '4px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  {t('Skeletal Muscle (%)')}
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={newHealthReading.skeletalMusclePercentage}
                  onChange={(e) => handleHealthReadingInputChange('skeletalMusclePercentage', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid var(--gray-medium)',
                    borderRadius: '4px'
                  }}
                />
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowAddHealthReadingModal(false);
                  setError('');
                }}
                style={{ padding: '10px 20px' }}
              >
                {t('Cancel')}
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAddHealthReading}
                style={{ padding: '10px 20px' }}
              >
                {t('Add Reading')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerDashboard;
